import { AuthBrandTagline } from '../components/AuthBrandTagline';
import {displayInputDigits, normalizeInputDigits} from '../utils/localizedInput';
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from '../components/NmButton';
import { NmInput } from '../components/NmInput';
import { NmCard } from '../components/NmCard';
import logoUrl from '@assets/logo.png';
import { isRegistrationIncompleteError } from '../utils/authErrors';

type LoginStep = 'mobile' | 'method' | 'password' | 'otp';

interface LoginPageProps {
  onLogin: (mobile: string, password: string) => Promise<void>;
  onLoginByOtp: (mobile: string, otp: string) => Promise<void>;
  onSendOtp: (mobile: string) => Promise<void>;
  onCheckMobile: (mobile: string) => Promise<{ exists: boolean; completed: boolean }>;
  /** Pre-fill mobile when switching from register tab */
  initialMobile?: string;
  /** Render only the form (no outer shell) — used inside AuthPage */
  embedded?: boolean;
  onSwitchToRegister?: (mobile?: string) => void;
  onNeedsRegistration?: (mobile: string, token: string) => void;
}

/** Multi-step login screen: mobile check → password or OTP path */
export function LoginPage({
  onLogin,
  onLoginByOtp: _onLoginByOtp,
  onSendOtp: _onSendOtp,
  onCheckMobile,
  initialMobile = '',
  embedded = false,
  onSwitchToRegister,
  onNeedsRegistration,
}: LoginPageProps) {
  const { t, i18n } = useTranslation();
  const displayPhone = (value: string) => displayInputDigits(value, i18n.resolvedLanguage ?? i18n.language);
  const [step, setStep] = useState<LoginStep>('mobile');
  const [mobile, setMobile] = useState(initialMobile);
  const [password, setPassword] = useState('');
  // OTP login disabled for now
  // const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // const [otpSent, setOtpSent] = useState(false);

  const clearError = () => setError('');

  const handleCheckMobile = useCallback(async () => {
    if (!mobile.trim()) {
      setError(t('login.error.mobileRequired'));
      return;
    }
    clearError();
    setLoading(true);
    try {
      const result = await onCheckMobile(mobile.trim());

      if (!result.exists) {
        setError(t('login.error.accountNotFound'));
        return;
      }

      // OTP login disabled for now — go straight to password
      setStep('password');
    } catch (e) {
      setError(t('login.error.serverError'));
    } finally {
      setLoading(false);
    }
  }, [mobile, onCheckMobile, t]);

  // OTP login disabled for now
  // const handleSendOtp = useCallback(async () => {
  //   clearError();
  //   setLoading(true);
  //   try {
  //     await onSendOtp(mobile.trim());
  //     setOtpSent(true);
  //     setStep('otp');
  //   } catch {
  //     setError('Failed to send OTP. Please try again.');
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [mobile, onSendOtp]);

  const handlePasswordLogin = useCallback(async () => {
    if (!password.trim()) {
      setError(t('login.error.passwordRequired'));
      return;
    }
    clearError();
    setLoading(true);
    try {
      await onLogin(mobile.trim(), password.trim());
    } catch (e) {
      if (isRegistrationIncompleteError(e)) {
        onNeedsRegistration?.(e.mobile, e.token);
        return;
      }
      setError(t('login.error.passwordIncorrect'));
    } finally {
      setLoading(false);
    }
  }, [mobile, password, onLogin, onNeedsRegistration, t]);

  // const handleOtpLogin = useCallback(async () => {
  //   if (otp.trim().length < 4) {
  //     setError('Please enter the OTP code.');
  //     return;
  //   }
  //   clearError();
  //   setLoading(true);
  //   try {
  //     await onLoginByOtp(mobile.trim(), otp.trim());
  //   } catch {
  //     setError('Invalid or expired OTP. Please try again.');
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [mobile, otp, onLoginByOtp]);

  const formContent = (
    <>
          {/* Step: Mobile */}
          {step === 'mobile' && (
            <div style={styles.form} className="animate-fadein">
              <h3 style={styles.stepTitle}>{t('login.stepTitle.mobile')}</h3>
              <NmInput
                label={t('login.field.mobileNumber')}
                type="tel"
                placeholder={displayPhone(t('login.field.mobilePlaceholder'))}
                value={displayPhone(mobile)}
                onChange={(e) => {
                  setMobile(normalizeInputDigits(e.target.value));
                  clearError();
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleCheckMobile()}
                autoFocus
                icon={<PhoneIcon />}
                error={error}
              />
              <NmButton
                variant="primary"
                size="lg"
                loading={loading}
                onClick={handleCheckMobile}
                style={{ marginTop: 8, width: '100%' }}
              >
                {t('login.action.continue')}
              </NmButton>
              {error === t('login.error.accountNotFound') && onSwitchToRegister && (
                <button
                  type="button"
                  onClick={() => onSwitchToRegister(mobile.trim())}
                  style={styles.linkBtn}
                >
                  {t('register.createAccountLink')}
                </button>
              )}
            </div>
          )}

          {/* OTP login disabled for now
          {step === 'method' && (
            <div style={styles.form} className="animate-fadein">
              <h3 style={styles.stepTitle}>How would you like to sign in?</h3>
              <p className="auth-mobile-number" dir="ltr" style={{ fontSize: 'var(--fs-small)', color: 'var(--fg-3)', marginBottom: 16 }}>
                {displayPhone(mobile)}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <NmButton
                  variant="primary"
                  size="lg"
                  onClick={() => setStep('password')}
                  style={{ width: '100%' }}
                >
                  Sign in with Password
                </NmButton>
                <NmButton
                  variant="neutral"
                  size="lg"
                  loading={loading}
                  onClick={handleSendOtp}
                  style={{ width: '100%' }}
                >
                  Sign in with OTP
                </NmButton>
              </div>
              <button onClick={() => setStep('mobile')} style={styles.backBtn}>
                ← Change number
              </button>
            </div>
          )}
          */}

          {/* Step: Password */}
          {step === 'password' && (
            <div style={styles.form} className="animate-fadein">
              <h3 style={styles.stepTitle}>{t('login.stepTitle.password')}</h3>
              <p className="auth-mobile-number" dir="ltr" style={{ fontSize: 'var(--fs-small)', color: 'var(--fg-3)', marginBottom: 16 }}>
                {displayPhone(mobile)}
              </p>
              <NmInput
                label={t('login.field.password')}
                type="password"
                placeholder={t('login.field.passwordPlaceholder')}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearError();
                }}
                onKeyDown={(e) => e.key === 'Enter' && handlePasswordLogin()}
                autoFocus
                error={error}
              />
              <NmButton
                variant="primary"
                size="lg"
                loading={loading}
                onClick={handlePasswordLogin}
                style={{ marginTop: 8, width: '100%' }}
              >
                {t('login.action.signIn')}
              </NmButton>
              <button onClick={() => setStep('mobile')} style={styles.backBtn}>
                {t('login.action.changeNumber')}
              </button>
            </div>
          )}

          {/* OTP login disabled for now
          {step === 'otp' && (
            <div style={styles.form} className="animate-fadein">
              <h3 style={styles.stepTitle}>Enter verification code</h3>
              <p style={{ fontSize: 'var(--fs-small)', color: 'var(--fg-3)', marginBottom: 16 }}>
                Code sent to {displayPhone(mobile)}
              </p>
              <NmInput
                label="OTP Code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value.replace(/\D/g, ''));
                  clearError();
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleOtpLogin()}
                autoFocus
                error={error}
              />
              <NmButton
                variant="primary"
                size="lg"
                loading={loading}
                onClick={handleOtpLogin}
                style={{ marginTop: 8, width: '100%' }}
              >
                Verify
              </NmButton>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                <button onClick={() => setStep('method')} style={styles.backBtn}>
                  ← Back
                </button>
                <button onClick={handleSendOtp} style={styles.backBtn}>
                  Resend code
                </button>
              </div>
            </div>
          )}
          */}
    </>
  );

  if (embedded) {
    return <div style={styles.form}>{formContent}</div>;
  }

  return (
    <div style={styles.root}>
      <div style={styles.blobTop} />
      <div style={styles.blobBottom} />

      <div style={styles.centerWrap}>
        <NmCard variant="raised-lg" style={styles.card}>
          <div style={styles.logoRow}>
            <img src={logoUrl} alt="VO2Peaks" style={styles.brandLogo} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <AuthBrandTagline style={styles.brandTagline} />
            </div>
          </div>

          <div className="nm-divider" />
          {formContent}
        </NmCard>
      </div>
    </div>
  );
}

// ─── Inline Styles ────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  root: {
    width: '100%',
    height: '100%',
    background: 'var(--surface-0)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  blobTop: {
    position: 'absolute',
    top: -120,
    right: -120,
    width: 360,
    height: 360,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(223,32,99,0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  blobBottom: {
    position: 'absolute',
    bottom: -100,
    left: -100,
    width: 280,
    height: 280,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(31,174,99,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  centerWrap: {
    width: '100%',
    maxWidth: 420,
    padding: '0 24px',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--sp-5)',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--sp-4)',
  },
  brandLogo: {
    width: 96,
    height: 32,
    padding: 6,
    borderRadius: 6,
    background: '#e8ecf2',
    objectFit: 'contain' as const,
    flexShrink: 0,
  },
  brandName: {
    fontSize: 'var(--fs-h3)',
    fontFamily: 'var(--font-display)',
    fontWeight: 800,
    margin: 0,
  },
  brandTagline: {
    fontSize: 'var(--fs-body)',
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    color: 'var(--fg-2)',
    margin: 0,
    lineHeight: 1.35,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--sp-4)',
  },
  stepTitle: {
    fontSize: 'var(--fs-h4)',
    marginBottom: 4,
  },
  backBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--fg-3)',
    fontSize: 'var(--fs-small)',
    padding: '4px 0',
    textAlign: 'left' as const,
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--vo2-pink-500)',
    fontSize: 'var(--fs-small)',
    padding: '4px 0',
    textAlign: 'center' as const,
    width: '100%',
  },
};

// ─── Icons ────────────────────────────────────────────────────────────────────
function PhoneIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.84a16 16 0 0 0 6.22 6.22l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}
